/**
 * Canonical, role-qualified plugin identity catalog.
 *
 * Both CLI and DOM adapters consume this list so registration identity
 * (id + role + aliases) cannot drift between adapters.
 * Adapters still own the concrete plugin objects (cells, legends,
 * labels) — this catalog only carries identity tuples.
 *
 * The catalog is composed from per-type modules under
 * `tento/tento-lona-js/sheets/src/plugins/` — one file per `(id, role)`. Adding a
 * new spec means: create the per-type file, register it in
 * `plugins/per-type-specs.ts`, and the catalog picks it up automatically.
 */

import { PER_TYPE_SPECS } from "../plugins/per-type-specs.ts";
import type { RenderPluginSpec } from "./render-plugin-core.ts";
import { deepFrozen } from "../core/frozen-collections.ts";

/**
 * Canonical role-qualified plugin identities.
 *
 * CLI and DOM adapters wrap each entry into their concrete plugin
 * implementation. Adding a new canonical id, alias, or legacy id
 * here ripples to both adapters via their registry construction.
 */
export const BUILTIN_RENDER_PLUGIN_SPECS: readonly RenderPluginSpec[] =
  PER_TYPE_SPECS;

/**
 * The renderer row types the rows side hydrates (`instantiateRenderer` builds
 * a variant for each) that no front end registers a render plugin for:
 * `column.timeline`, the `span` / `text` / `number` marks of a timeline and
 * `grid.text`. With the renderer ids of `BUILTIN_RENDER_PLUGIN_SPECS` they
 * are every `Renderer.*` row type of the generated catalog
 * (`sheets/src/render/render-plugin-catalog.deno.test.ts` holds the two lists to it).
 */
export const ROWS_ONLY_RENDERER_IDS: readonly string[] = deepFrozen([
  "column.timeline",
  "timeline[row].span",
  "timeline[row].text",
  "timeline[row].number",
  "timeline[column].span",
  "timeline[column].text",
  "timeline[column].number",
  "grid.text",
]);
